import { useFormik } from "formik";
import { useState } from "react";
import { NumericFormat } from "react-number-format";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { ModalComponent } from "../..";
import { ModalControls } from "../../../../customHooks/useModalControl";
import {
  cleanHashes,
  formatDate,
  parseDateStringToDate,
} from "../../../../helperFunctions";
import {
  useLazyGetUserProfileQuery,
  useSendBvnOtpMutation,
  useValidateBVNMutation,
} from "../../../../redux/features/auth/auth.api";
import { useAppSelector } from "../../../../redux/hooks";
import { BaseAPIResponse } from "../../../../utils/types";
import { Button } from "../../../buttons/button";
import { InformationCard } from "../../../cards/infoCard";
import { DateInput } from "../../../inputs/dateInput";
import { TextInput } from "../../../inputs/textInput";
import { NotificationModal } from "../notificationModal";
import { SuccessModal } from "../successModal";

export const BvnVerificationFlowModal = ({
  modalControls,
  onSuccess,
}: {
  modalControls: ModalControls;
  onSuccess: () => void;
}) => {
  const [step, setStep] = useState<1 | 2>(1);

  const navigate = useNavigate();

  const { profile } = useAppSelector((state) => state.auth);

  const { modal, modalOpen, handleModalClose, handleModalOpen } = modalControls;

  const [fetchProfile] = useLazyGetUserProfileQuery();

  const [validateBvn, { isLoading: validatingBvn, error: validateBvnError }] =
    useValidateBVNMutation();

  const [sendBvnOtp, { isLoading: sendingBvnOtp, data: bvnOtpDetails }] =
    useSendBvnOtpMutation();

  const {
    values,
    touched,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isValid,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues: {
      bvn: "",
      firstName: cleanHashes(profile?.FirstName) ?? "",
      middleName: cleanHashes(profile?.MiddleName) ?? "",
      lastName: cleanHashes(profile?.LastName) ?? "",
      dateOfBirth: cleanHashes(profile?.DateOfBirth)
        ? parseDateStringToDate(
            cleanHashes(profile?.DateOfBirth),
            "YYYY-MM-DD",
            true
          )
        : undefined,
      code: "",
    },
    validationSchema: Yup.object().shape({
      bvn: Yup.string()
        .length(11, "BVN must be 11 digits")
        .required("BVN is required"),
      firstName: Yup.string().required("First name is required"),
      middleName: Yup.string(),
      lastName: Yup.string().required("Last name is required"),
      dateOfBirth: Yup.string().required("Date of Birth is required"),
      code:
        step === 2 ? Yup.string().required("Code is required") : Yup.string(),
    }),
    onSubmit: async (vals) => {
      if (step === 1) {
        const resp = await sendBvnOtp({
          bvn: vals.bvn,
        });

        if (resp?.data?.success) {
          setStep(2);
        }
      } else if (step === 2) {
        const resp = await validateBvn({
          bvn: vals.bvn,
          first_name: vals.firstName,
          last_name: vals.lastName,
          date_of_birth: formatDate(vals.dateOfBirth, "YYYY-MM-DD"),
          code: vals.code,
        });

        if (resp?.data?.success) {
          fetchProfile();
          handleModalOpen("bvnVerificationSuccessModal");
        } else {
          handleModalOpen("bvnVerificationFailedModal");
        }
      }
    },
  });

  const reset = () => {
    setStep(1);
    resetForm();
  };

  return (
    <>
      <ModalComponent
        isModalOpen={
          !profile?.bvn &&
          (!profile?.BVNNumber ||
            profile?.BVNNumber?.replaceAll(" ", "")?.toLowerCase() ===
              "undefined") &&
          !modalOpen
        }
        showMobileTopBar
        showHeader
        onModalClose={() => {
          reset();
          navigate("/app/settings?tab=profile");
        }}
        closeOnOutsideClick={false}
        title={step === 1 ? `Complete KYC Verification` : "Enter Code"}
        subTitle={
          step === 2
            ? `A code was sent to your BVN registered phone number ${
                bvnOtpDetails?.data?.phone
              } ${
                bvnOtpDetails?.data?.email
                  ? `and email ${bvnOtpDetails?.data?.email}`
                  : ``
              }`
            : undefined
        }
        topTitle={false}
        childrenClassName="flex flex-col gap-4 justify-between"
        modalClassName="!min-h-[490px]"
        hasButtons={false}
      >
        {step === 1 ? (
          <>
            <InformationCard
              info={`We use your BVN only to verify your identity. By continuing, you consent to us securely verifying your BVN with our verification partners. Your BVN is protected and will not be used for any other purpose.`}
              size="mini"
              variant="purple"
            />

            <TextInput
              type="text"
              name="firstName"
              value={values?.firstName}
              placeholder="Your first name"
              onChange={handleChange}
              onBlur={handleBlur}
              hasIcon={false}
              iconSvg={undefined}
              errors={
                touched?.firstName && errors?.firstName ? errors?.firstName : ""
              }
              inputClass=""
              className=""
              containerClassName=""
              hasTitle
              title="First Name"
            />

            <TextInput
              type="text"
              name="lastName"
              value={values?.lastName}
              placeholder="Your last name"
              onChange={handleChange}
              onBlur={handleBlur}
              hasIcon={false}
              iconSvg={undefined}
              errors={
                errors?.lastName && touched?.lastName ? errors?.lastName : ""
              }
              inputClass=""
              className=""
              containerClassName=""
              hasTitle
              title="Last Name"
            />

            <DateInput
              name="dateOfBirth"
              date={values?.dateOfBirth}
              placeholder="Date of Birth"
              setDate={(val) => {
                setFieldValue("dateOfBirth", val?.toDate());
              }}
              onBlur={handleBlur}
              errors={
                errors?.dateOfBirth && touched?.dateOfBirth
                  ? errors?.dateOfBirth
                  : ""
              }
              title="Date of Birth"
              disableFuture
            />

            <NumericFormat
              suffix={undefined}
              prefix={undefined}
              decimalScale={0}
              thousandSeparator={false}
              allowLeadingZeros={true}
              customInput={TextInput}
              type="text"
              name={"bvn"}
              value={values?.bvn ?? ""}
              title="Bank Verification Number (BVN)"
              placeholder="Bank Verification Number"
              onBlur={handleBlur}
              onChange={handleChange}
              hasIcon={false}
              errors={errors?.bvn && touched?.bvn ? errors?.bvn : ""}
              inputClass=""
              className=""
              customIconPosition="left"
              hasTitle
            />

            <Button
              type="button"
              text="Proceed"
              variation="primary"
              onClick={() => {
                handleSubmit();
              }}
              disabled={!isValid || sendingBvnOtp}
              loading={sendingBvnOtp}
            />
          </>
        ) : undefined}

        {step === 2 ? (
          <>
            <TextInput
              type="text"
              name={"code"}
              value={values?.code ?? ""}
              placeholder="Enter code"
              onBlur={handleBlur}
              onChange={handleChange}
              hasIcon={false}
              errors={errors?.code && touched?.code ? errors?.code : ""}
              inputClass=""
              className=""
              customIconPosition="left"
              hasTitle={false}
            />

            <Button
              type="button"
              text="Verify"
              variation="primary"
              onClick={() => {
                handleSubmit();
              }}
              disabled={!isValid || validatingBvn}
              loading={validatingBvn}
            />
          </>
        ) : undefined}
      </ModalComponent>

      <SuccessModal
        message={`BVN verified successfully`}
        isModalOpen={modalOpen && modal === "bvnVerificationSuccessModal"}
        onModalClose={() => {
          reset();
          handleModalClose();
        }}
      />

      <NotificationModal
        isModalOpen={modal === "bvnVerificationFailedModal" && modalOpen}
        onModalClose={() => {
          reset();
          handleModalClose();
        }}
        title={
          <>
            <span className="text-4xl">❌</span>
            <br />
            <br />
            BVN Verification Failed
          </>
        }
        subText={
          <>{(validateBvnError as { data: BaseAPIResponse })?.data?.error}</>
        }
      />
    </>
  );
};
